<template>
  <div>
    <popover
      boundary="viewport"
      @hide="beforePopoverHide"
      @shown="onPopoverShown"
      :target="target"
      triggers="click blur"
      custom-class="initiative-fields-menu-popover"
    >
      <div class="popover-container text-medium">
        <div class="scrollable-region">
          <div class="menu-header">
            <div class="d-flex align-center justify-content-between">
              <div class="text-normal bold" role="heading">Show/Hide Fields</div>
              <link-button
                class="p-0 text-medium"
                link-color="brand"
                @click="triggerAddition"
                v-if="autoSaveColumnSettings"
                >Create New
              </link-button>
            </div>
            <div class="pt-2 pb-3 search-container d-flex">
              <input
                class="border flex-grow-1 py-1 pl-2"
                placeholder="Search"
                type="text"
                autocomplete="off"
                v-model="searchText"
                v-autofocus
              />
            </div>
          </div>
          <div class="text-normal bold mb-2" v-if="enabledCustomFields.length > 0">
            Custom Fields
          </div>

          <initiative-fields-menu-item
            v-for="field in enabledCustomFields"
            :field="field"
            :team-id="teamId"
            :project-id="projectId"
            :org-id="orgId"
            :show-remove-icon="showRemoveIcon"
            @select="onFieldSelect(field, $event)"
            @remove="onRemoveCustomField(field)"
            @edit="onEditCustomField(field)"
            :key="field.id"
          />
          <div class="text-normal bold mt-3 mb-2" v-if="systemFields.length > 0">
            System Fields
          </div>
          <initiative-fields-menu-item
            v-for="field in systemFields"
            :field="field"
            :team-id="teamId"
            :project-id="projectId"
            :org-id="orgId"
            :key="field.name"
            @select="onFieldSelect(field, $event)"
          />
          <div class="spacer"></div>
        </div>
      </div>
      <div class="popover-footer text-medium bold text-brand" v-if="autoSaveColumnSettings">
        <link-button
          @click="showCustomLibrary = true"
          link-color="brand"
          class="p-0 bold text-medium"
          >Use from Library
        </link-button>
      </div>
    </popover>
    <remove-custom-field-confirm-dialog
      ref="removeDialog"
      :team-id="teamId"
      :project-id="projectId"
      :org-id="orgId"
      :custom-field="currentCustomField"
      @removed="refreshCFs"
      @hidden="preventMenuDismissal = false"
      v-if="showRemoveIcon"
    />
    <custom-field-editor-modal
      :custom-field="currentCustomField"
      :org-id="orgId"
      :project-id="projectId"
      v-model="showCustomFieldEditor"
      :team-id="teamId"
      :enable-after-creation="true"
      :display-after-creation="true"
      @created="refreshCFs"
      @saved="refreshCFs"
      v-if="showCustomFieldEditor"
    />
    <custom-fields-library-modal
      :org-id="orgId"
      :project-id="projectId"
      v-model="showCustomLibrary"
      :team-id="teamId"
      :add-to-display-fields="true"
      @added="onCustomFieldAttachment"
      v-if="showCustomLibrary"
    />
    <max-cfs-paywall-modal
      v-model="isPaywallModalShown"
      :org-id="orgId"
      v-if="isPaywallModalShown"
    />
  </div>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton.vue'
import CustomFieldEditorModal from '@/components/initiative/CustomFieldEditorModal.vue'
import CustomFieldsLibraryModal from '@/components/initiative/CustomFieldsLibraryModal.vue'
import InitiativeFieldsMenuItem from '@/components/initiative/InitiativeFieldsMenuItem.vue'
import RemoveCustomFieldConfirmDialog from '@/components/initiative/RemoveCustomFieldConfirmDialog.vue'
import MaxCfsPaywallModal from '@/components/paywalls/MaxCfsPaywallModal.vue'
import Popover from '@/components/Popover.vue'
import { featureFlags } from '@/constants/featureFlags'
import {
  INITIATIVE_COMPUTED_FIELD_NAME,
  INITIATIVE_MANDATORY_FIELD,
  InitiativeFieldType,
  SYSTEM_FIELDS,
  TEAM_LEVEL_INITIATIVE_COMPUTED_FIELDS
} from '@/constants/initiative'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import showAPIError from '@/utils/showAPIError'
import { mapActions, mapGetters } from 'vuex'
import * as R from 'ramda'

/**
 * This menu show the list of available Custom fields and System fields. User can associate new Custom/System fields to Project/Team by using this menu. It also enables users to add new column to the Initiatives Table.
 */
export default {
  name: 'InitiativeFieldsMenu',
  props: ['target', 'teamId', 'projectId', 'orgId', 'displayFields', 'autoSaveColumnSettings'], // displayFields is mandatory when autoSaveColumnSettings is false
  mixins: [ComponentIdMixin, FeaturesMixin],
  components: {
    RemoveCustomFieldConfirmDialog,
    MaxCfsPaywallModal,
    CustomFieldsLibraryModal,
    CustomFieldEditorModal,
    InitiativeFieldsMenuItem,
    LinkButton,
    Popover
  },
  data() {
    return {
      searchText: '',
      showCustomFieldEditor: false,
      preventMenuDismissal: false,
      isPaywallModalShown: false,
      showCustomLibrary: false,
      isCFAdditionAllowed: false,
      currentCustomField: null
    }
  },
  computed: {
    ...mapGetters([
      'initiativeEnabledCustomFieldsByTeamId',
      'initiativeEnabledCustomFieldsByProjectId',
      'initiativeDisplayFieldsByTeamId',
      'initiativeDisplayFieldsByProjectId'
    ]),
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    showRemoveIcon() {
      return this.autoSaveColumnSettings
    },
    isProject() {
      return !!this.projectId
    },
    isProjectEnabledInTeam() {
      const isFeatureAllowed = this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_PROJECTS)
      if (this.teamId) return isFeatureAllowed && this.team.preferences?.enableProjects
      else return isFeatureAllowed
    },
    systemFields() {
      let fields = SYSTEM_FIELDS
      if (!this.isProjectEnabledInTeam || this.projectId) {
        // Don't show "Project" option if project is not enabled or view is opened in the context of a Project
        fields = fields.filter(field => field.name !== INITIATIVE_COMPUTED_FIELD_NAME.PROJECT)
      }
      return fields
        .filter(
          field =>
            !this.isProject ||
            !TEAM_LEVEL_INITIATIVE_COMPUTED_FIELDS.some(f => f.name === field.name)
        )
        .filter(this.satisfySearch)
        .map(field => {
          const displayField = this.displayCustomFields?.find(
            f => f.standardFieldName === field.name
          )
          return {
            ...field,
            isDisplayedField: !!displayField,
            displayFieldId: displayField?.id,
            isDisabled: field.name === INITIATIVE_MANDATORY_FIELD
            // isDisplayedField denotes if this field is supposed to be shown in table view
          }
        })
    },
    enabledCustomFields() {
      let fields
      if (!this.autoSaveColumnSettings) {
        fields = this.$store.state.orgCustomFields[this.orgId] || []
      } else if (this.isProject) {
        fields = this.initiativeEnabledCustomFieldsByProjectId(this.projectId) || []
      } else {
        fields = this.initiativeEnabledCustomFieldsByTeamId(this.teamId) || []
      }
      fields = fields
        .map(field => {
          const displayField = this.displayCustomFields?.find(
            f => f.customFieldDefinitionId === field.id
          )
          return {
            ...field,
            title: field.name,
            fieldType: InitiativeFieldType.CUSTOM_FIELD,
            isDisplayedField: !!displayField,
            displayFieldId: displayField?.id
          }
        })
        .filter(this.satisfySearch)
      return R.sort(R.ascend(R.prop('projectId')), fields)
    },
    displayCustomFields() {
      if (!this.autoSaveColumnSettings) {
        const customFields = this.displayFields.customFieldDefinitionIds || []
        const systemFields = this.displayFields.standardFieldNames || []
        this.displayFields.customFieldDefinitionIds?.map(customFieldDefinitionId => ({
          customFieldDefinitionId
        }))
        return [
          ...customFields.map(customFieldDefinitionId => ({ customFieldDefinitionId })),
          ...systemFields.map(standardFieldName => ({ standardFieldName }))
        ]
      }
      if (this.isProject) {
        return this.initiativeDisplayFieldsByProjectId(this.projectId)
      }
      return this.initiativeDisplayFieldsByTeamId(this.teamId)
    }
  },
  methods: {
    ...mapActions([
      'getEnabledCustomFields',
      'getDisplayInitiativeFields',
      'addDisplayInitiativeField',
      'removeDisplayInitiativeField'
    ]),
    satisfySearch(field) {
      return field.title.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
    },
    refreshCFs() {
      this.refreshEnabledCustomFields()
      this.refreshDisplayFields()
      this.setCFAdditionAllowedFlag()
    },
    refreshDisplayFields() {
      this.getDisplayInitiativeFields({
        orgId: this.orgId,
        teamId: this.teamId,
        projectId: this.projectId
      })
    },
    refreshEnabledCustomFields() {
      this.getEnabledCustomFields({
        orgId: this.orgId,
        teamId: this.teamId,
        projectId: this.projectId
      })
    },
    beforePopoverHide(e) {
      if (this.preventMenuDismissal) {
        e.preventDefault()
      }
    },
    onPopoverShown() {
      this.refreshCFs()
    },
    onRemoveCustomField(field) {
      this.currentCustomField = field
      this.preventMenuDismissal = true
      this.$refs.removeDialog.show()
    },
    onEditCustomField(field) {
      this.currentCustomField = field
      this.showCustomFieldEditor = true
    },
    addRemoveField(fields, fieldId, isSelected) {
      fields = new Set(fields)
      if (isSelected) {
        fields.add(fieldId)
      } else {
        fields.delete(fieldId)
      }
      return [...fields]
    },
    notifyFieldChangeToParent(field, isSelected) {
      let displayFields = this.displayFields
      if (field.fieldType === InitiativeFieldType.CUSTOM_FIELD) {
        displayFields = {
          ...displayFields,
          customFieldDefinitionIds: this.addRemoveField(
            this.displayFields.customFieldDefinitionIds,
            field.id,
            isSelected
          )
        }
      } else {
        displayFields = {
          ...displayFields,
          standardFieldNames: this.addRemoveField(
            this.displayFields.standardFieldNames,
            field.name,
            isSelected
          )
        }
      }
      this.$emit('enable-cfs', displayFields)
    },
    async onFieldSelect(field, isSelected) {
      if (!this.autoSaveColumnSettings) {
        this.notifyFieldChangeToParent(field, isSelected)
        return
      }
      try {
        const isCF = field.fieldType === InitiativeFieldType.CUSTOM_FIELD
        if (isSelected) {
          const payload = {}
          if (isCF) {
            payload.customFieldDefinitionIds = [field.id]
          } else {
            payload.standardFieldNames = [field.name]
          }
          await this.addDisplayInitiativeField({
            orgId: this.orgId,
            teamId: this.teamId,
            projectId: this.projectId,
            payload
          })
        } else {
          const payload = {}
          if (isCF) {
            payload.customFieldDefinitionId = field.id
          } else {
            payload.standardFieldName = field.name
          }
          await this.removeDisplayInitiativeField({
            orgId: this.orgId,
            teamId: this.teamId,
            projectId: this.projectId,
            displayFieldId: field.displayFieldId
          })
        }
        this.getDisplayInitiativeFields({
          orgId: this.orgId,
          teamId: this.teamId,
          projectId: this.projectId
        })
      } catch (e) {
        showAPIError(e)
      }
    },
    async onCustomFieldAttachment() {
      this.refreshEnabledCustomFields()
      this.refreshDisplayFields()
    },
    async setCFAdditionAllowedFlag() {
      this.isCFAdditionAllowed = await this.satisfyFeature(
        this.orgId,
        featureFlags.MAX_CUSTOM_FIELD_COUNT
      )
    },
    triggerAddition() {
      this.currentCustomField = null
      if (!this.isCFAdditionAllowed) {
        this.isPaywallModalShown = true
      } else {
        this.showCustomFieldEditor = true
      }
    }
  },
  mounted() {
    this.setCFAdditionAllowedFlag()
    this.refreshCFs()
  }
}
</script>

<style scoped lang="scss">
@import '~@/styles/_styleConfigurations.scss';

$sideSpacing: 20px;
.popover-container {
  min-width: 280px;
  padding: $sideSpacing 0 0 $sideSpacing;
}

.scrollable-region {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: $sideSpacing - 10px; // reducing width of scroll i.e 10px
}

input {
  border-radius: 4px;
}

.popover-footer {
  padding: 14px $sideSpacing;
  background-color: $brandSecondary;
}

.menu-header {
  @include sticky(0, null);
  background-color: $documentColor;
}

.spacer {
  height: 10px;
  width: 100%;
}
</style>
<style lang="scss">
.initiative-fields-menu-popover {
  overflow: hidden;

  .popover-body {
    padding: 0;
  }
}
</style>
